import React, { useEffect, useMemo, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { WaTemplate } from "@/types/whatsapp";

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  dealId: string | null;
  /** Suggested value for the first variable (usually the buyer's name) */
  defaultName: string;
  sending: boolean;
  onSend: (payload: { template_id: string; template_params: string[]; quote_id?: string; media?: any }) => void;
}

const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/** Pick an approved template, fill its variables, and preview what the buyer will get */
export function TemplateDialog({ open, onOpenChange, dealId, defaultName, sending, onSend }: TemplateDialogProps) {
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [values, setValues] = useState<string[]>([]);
  const [quotes, setQuotes] = useState<{ id: string; display_number: string; grand_total: number; quote_status: string }[]>([]);
  const [quoteId, setQuoteId] = useState("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/api/WhatsApp/Templates")
      .then((res) => setTemplates(unwrapArray<WaTemplate>(res)))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
    if (dealId) {
      api
        .get(`/api/Quotation/List?deal_id=${dealId}&page_size=100`)
        .then((res) => setQuotes(unwrapPage<any>(res).rows.filter((q) => ["APPROVED", "SENT", "ACCEPTED"].includes(q.quote_status))))
        .catch(() => setQuotes([]));
    }
    setSelectedId("");
    setValues([]);
    setQuoteId("");
    setFile(null);
  }, [open, dealId]);

  const t = templates.find((x) => x.id === selectedId);
  const variables = useMemo(
    () => (t ? [...t.header_variables.map((v) => `Header {{${v}}}`), ...t.body_variables.map((v) => `{{${v}}}`)] : []),
    [t]
  );
  const mediaHeader = t && ["DOCUMENT", "IMAGE", "VIDEO"].includes(t.header_format || "") ? t.header_format : null;

  const choose = (id: string) => {
    setSelectedId(id);
    const tpl = templates.find((x) => x.id === id);
    const count = (tpl?.header_variables.length || 0) + (tpl?.body_variables.length || 0);
    setValues(Array.from({ length: count }, (_, i) => (i === (tpl?.header_variables.length || 0) ? defaultName : "")));
  };

  const fill = (text: string, names: string[], offset: number) =>
    names.reduce((out, n, i) => out.replaceAll(new RegExp(`\\{\\{\\s*${n}\\s*\\}\\}`, "g") as any, values[offset + i] || `{{${n}}}`), text);

  const preview = t
    ? [t.header_text ? fill(t.header_text, t.header_variables, 0) : "", fill(t.body_text, t.body_variables, t.header_variables.length), t.footer_text || ""]
        .filter(Boolean)
        .join("\n\n")
    : "";

  const ready =
    t && values.every((v) => v.trim()) && (!mediaHeader || (mediaHeader === "DOCUMENT" ? Boolean(quoteId || file) : Boolean(file)));

  const submit = async () => {
    if (!t || !ready) return;
    const payload: any = { template_id: t.id, template_params: values.map((v) => v.trim()) };
    if (mediaHeader === "DOCUMENT" && quoteId) payload.quote_id = quoteId;
    else if (mediaHeader && file) {
      payload.media = { file_name: file.name, content_type: file.type || "application/octet-stream", content_base64: await readAsBase64(file) };
    }
    onSend(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send WhatsApp template</DialogTitle>
          <DialogDescription>
            Templates are pre-approved by Meta and can be sent any time, even after the 24-hour reply window.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <div className="py-6 text-sm text-muted-foreground">
            No approved templates yet. Create them in Meta's WhatsApp Manager, then an admin clicks "Sync templates" in Settings → WhatsApp.
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-3.5">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-semibold">Template</Label>
                <Select value={selectedId} onValueChange={choose}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((x) => (
                      <SelectItem key={x.id} value={x.id}>
                        {x.name} ({x.language})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {t && (
                  <Badge variant="outline" className="text-[11px] mt-1">
                    {t.category}
                  </Badge>
                )}
              </div>
              {variables.map((label, i) => (
                <div key={label} className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold">{label}</Label>
                  <Input
                    value={values[i] || ""}
                    onChange={(e) => setValues((list) => list.map((v, idx) => (idx === i ? e.target.value : v)))}
                  />
                </div>
              ))}
              {mediaHeader === "DOCUMENT" && quotes.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold">Attach quotation PDF</Label>
                  <Select value={quoteId} onValueChange={(v) => (setQuoteId(v), setFile(null))}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose a quotation" />
                    </SelectTrigger>
                    <SelectContent>
                      {quotes.map((q) => (
                        <SelectItem key={q.id} value={q.id}>
                          {q.display_number} · {formatINR(q.grand_total)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {mediaHeader && (
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm font-semibold">
                    {mediaHeader === "DOCUMENT" && quotes.length > 0 ? "…or upload a document" : `Header ${mediaHeader.toLowerCase()}`}
                  </Label>
                  <Input
                    type="file"
                    accept={mediaHeader === "IMAGE" ? "image/jpeg,image/png" : mediaHeader === "VIDEO" ? "video/mp4" : undefined}
                    onChange={(e) => (setFile(e.target.files?.[0] || null), setQuoteId(""))}
                  />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Preview</Label>
              <div className="wa wa-wallpaper rounded-xl p-4 min-h-40">
              <div className="wa-bubble wa-bubble-out wa-tail-out rounded-lg px-2.5 py-2 text-[14.2px] whitespace-pre-wrap ml-auto max-w-[95%] w-fit">
                {mediaHeader && <div className="text-[11px] text-muted-foreground mb-1">[{mediaHeader.toLowerCase()} attached]</div>}
                {preview || <span className="text-muted-foreground">Choose a template to preview it.</span>}
                {t?.buttons?.length ? (
                  <div className="mt-2 pt-2 border-t border-black/10 space-y-1">
                    {t.buttons.map((b: any, i: number) => (
                      <div key={i} className="text-center text-[14px] text-[#027eb5] font-medium">
                        {b.text}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!ready || sending} className="gap-1.5">
            {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            Send template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
